import { createContext, useContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { initGA } from '@/lib/ga';
import { useNavigate, useLocation } from 'react-router-dom';

interface CookieConsentContextType {
  hasConsent: boolean;
  setConsent: (accepted: boolean) => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const CONSENT_COOKIE_NAME = 'cookie_consent';
const CONSENT_COOKIE_DURATION = 365; // Tage

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const [hasConsent, setHasConsent] = useState<boolean>(() => {
    return Cookies.get(CONSENT_COOKIE_NAME) === 'true';
  });
  const navigate = useNavigate();
  const location = useLocation();

  const setConsent = (accepted: boolean) => {
    if (accepted) {
      Cookies.set(CONSENT_COOKIE_NAME, 'true', {
        expires: CONSENT_COOKIE_DURATION,
        path: '/',
        sameSite: 'Lax',
        secure: true, // empfohlen für Production (HTTPS)
      });

      initGA();
    } else {
      Cookies.remove(CONSENT_COOKIE_NAME, { path: '/' });
    }

    setHasConsent(accepted);
  };

  // Init GA bei bestehendem Consent (z. B. nach Refresh)
  useEffect(() => {
    if (hasConsent) {
      initGA();
    }
  }, [hasConsent]);

  // Optionale Synchronisierung des Zustands zwischen Tabs
  useEffect(() => {
    const handleCookieConsentChange = () => {
      const consentFromCookie = Cookies.get(CONSENT_COOKIE_NAME) === 'true';
      setHasConsent(consentFromCookie);
    };

    window.addEventListener('storage', handleCookieConsentChange);
    return () => window.removeEventListener('storage', handleCookieConsentChange);
  }, []);

  return (
    <CookieConsentContext.Provider value={{ hasConsent, setConsent }}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextType {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}